import useAppNavigation from '@/hooks/useAppNavigation';
import { format } from 'date-fns';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTES } from '../../../constants/Constants';
import { setCookiePreferences } from '../../../redux/ducks/StorageDucks';
import type {
  CookiePreference,
  CookieSelectionType,
} from '../../../redux/ducks/StorageDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import CookieSelection from '../../layout/CookieSelection';

const defaultPreferences: CookieSelectionType = {
  all: false,
  allAnalytics: false,
  allAdvertisement: false,
  allPerformance: false,
  allFunctional: false,
  allOthers: false,
  onesignal: false,
  feedbackfish: false,
};

const lastPolicyUpdate = new Date('2025-04-04');

interface CookiePolicyProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const CookiePolicy: React.FC<CookiePolicyProps> = ({ ssrFetchedData }) => {
  const { t } = useTranslation();
  const { toLocalizedRoute } = useAppNavigation();
  const [preferences, setPreferences] = useState<CookieSelectionType | null>(
    null,
  );
  const [cachedPreferences, setCachedPreferences] =
    useState<CookieSelectionType | null>(null);
  const dispatch = useDispatch();

  const cookiePreferences = useSelector(
    (state: RootReducerType) => state.storage.cookiePreferences,
  );

  useEffect(() => {
    if (preferences === null) {
      return;
    }

    setCachedPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    if (cachedPreferences !== null) {
      // Save preference on redux
      const prefs: CookiePreference = {
        preferences: cachedPreferences,
        updated: Date.now(),
      };
      dispatch(setCookiePreferences(prefs));
      // if logged in update preferences
      // TODO
    }
  }, [cachedPreferences]);

  return (
    <div className="flex flex-col items-center p-4">
      <div className="mx-2 max-w-3xl md:mx-8">
        <section className="pb-6 font-semibold">
          <h1 className="mb-2 text-2xl font-black">Cookie Policy</h1>
          <h2>Last updated: {format(lastPolicyUpdate, 'do MMMM yyyy')}</h2>
        </section>
        <section>
          <div className="flex shrink-0 flex-row items-center justify-between">
            <span className="mb-8 text-xl font-black">
              About this cookie policy.
            </span>
          </div>
          <div className="mb-8 font-normal">
            <section>
              This Cookie Policy explains what cookies are and how we use them.
              You should read this policy to understand what cookies are, how we
              use them, the types of cookies we use i.e, the information we
              collect using cookies and how that information is used and how to
              control the cookie preferences. For further information on how we
              use, store and keep your personal data secure, see our{' '}
              <a
                data-test-id="cp-pp-link"
                href={toLocalizedRoute(ROUTES.PRIVACY_POLICY_ROUTE)}
                className="cursor-pointer text-sky-600 hover:text-sky-400"
              >
                Privacy Policy
              </a>
              .
            </section>
            <section className="mt-4">
              You can at any time change or withdraw your consent from the
              Cookie Declaration on this page. Learn more about who we are, how
              you can contact us and how we process personal data in our{' '}
              <a
                data-test-id="cp-pp-link"
                href={toLocalizedRoute(ROUTES.PRIVACY_POLICY_ROUTE)}
                className="cursor-pointer text-sky-600 hover:text-sky-400"
              >
                Privacy Policy
              </a>
              .
            </section>
          </div>
        </section>
        <section>
          <div className="flex shrink-0 flex-row items-center justify-between">
            <div className="mb-8 text-xl font-black">What are cookies?</div>
          </div>
          <div className="mb-8 font-normal">
            <section>
              Cookies are small text files that are stored on your device when
              you visit a website. They help the website function properly,
              improve security, and provide a better user experience. They also
              allow us to analyze how the website performs and identify areas
              for improvement.
            </section>
          </div>
        </section>
        <section>
          <div className="flex shrink-0 flex-row items-center justify-between">
            <div className="mb-8 text-xl font-black">
              How do we use cookies?
            </div>
          </div>
          <div className="mb-8 font-normal">
            <section>
              As most of the online services, our website uses first-party and
              third-party cookies for a number of purposes. The first-party
              cookies are mostly necessary for the website to function the right
              way. The third-party cookies used on our websites are used mainly
              for understanding how the website performs, how you interact with
              our website, keeping our services secure, providing advertisements
              that are relevant to you, and all in all providing you with a
              better and improved user experience and help speed up your future
              interactions with our website.
            </section>
          </div>
        </section>
        <section>
          <div className="flex shrink-0 flex-row items-center justify-between">
            <div className="mb-8 text-xl font-black">
              What types of cookies do we use?
            </div>
          </div>
          <div className="mb-8 font-normal">
            <section>
              <CookieSelection
                preferences={
                  preferences ||
                  cookiePreferences?.preferences ||
                  defaultPreferences
                }
                setPreferences={(p: CookieSelectionType) => {
                  setPreferences(p);
                }}
              />
            </section>
          </div>
        </section>
        <section>
          <div className="flex shrink-0 flex-row items-center justify-between">
            <div className="mb-8 text-xl font-black">
              How can I control the cookie preferences?
            </div>
          </div>
          <div className="mb-8 font-normal">
            <section>
              You can manage your cookies preferences in the{' '}
              <span className="font-semibold italic">
                What types of cookies do we use?
              </span>{' '}
              section of this page by clicking on the switch buttons and
              enabling or disabling the cookie according to your preferences.
            </section>
            <section className="mt-4">
              You can always change your preferences later through your browsing
              session, you can click on the{' '}
              <span className="font-semibold italic">Cookie Policy</span>{' '}
              section of the footer. This will display this page again enabling
              you to change your preferences or withdraw your consent entirely.
            </section>
            <section className="mt-4">
              In addition to this, different browsers provide different methods
              to block and delete cookies used by websites. You can change the
              settings of your browser to block/delete the cookies. To find out
              more out more on how to manage and delete cookies, visit{' '}
              <button
                aria-label="cookie-policy-all-about-cookies-btn"
                data-test-id="cookie-policy-all-about-cookies-btn"
                id="cookie-policy-all-about-cookies-btn"
                key="cookie-policy-all-about-cookies-btn"
                type="button"
                className="cursor-pointer text-sky-600 hover:text-sky-400"
                onClick={() => {
                  window.open(
                    'https://www.allaboutcookies.org',
                    '_blank',
                    'rel=noopener noreferrer',
                  );
                }}
              >
                allaboutcookies.org
              </button>
              .
            </section>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CookiePolicy;
